import { Icon } from '@sakhtar/design-system/react';
import type { DeepSearchProgressSpec } from '../types.js';

export interface DeepSearchCardProps {
  readonly spec: DeepSearchProgressSpec;
}

const SCOPE_LABELS: Record<'web' | 'company' | 'hybrid', string> = {
  web: 'وب آزاد',
  company: 'دانش و اسناد شرکت',
  hybrid: 'ترکیبی (وب و سازمان)',
};

export function DeepSearchCard({ spec }: DeepSearchCardProps) {
  const hasSteps = spec.steps.length > 0;
  const hasWebSources = spec.webSources && spec.webSources.length > 0;
  const hasContradictions = spec.contradictions && spec.contradictions.length > 0;

  return (
    <div className="ai-deep-search sk-card" aria-label="گزارش جست‌وجوی عمیق">
      <div className="ai-deep-search__head">
        <div className="ai-deep-search__title-group">
          <Icon name="search" size="sm" />
          <span className="ai-deep-search__title">فرآیند جست‌وجوی عمیق و چندمرحله‌ای</span>
        </div>
        <span className="sk-tag sk-tag--subtle ai-deep-search__scope">
          دامنه: {SCOPE_LABELS[spec.scope] ?? spec.scope}
        </span>
      </div>

      {hasSteps ? (
        <div className="ai-deep-search__steps">
          <div className="ai-deep-search__steps-label">مراحل و پرسش‌های فرعی بررسی‌شده:</div>
          <ol className="ai-deep-search__list">
            {spec.steps.map((step, idx) => (
              <li key={step.id || idx} className={`ai-deep-search__step ai-deep-search__step--${step.status}`}>
                <span className="ai-deep-search__step-status" aria-hidden="true">
                  {step.status === 'completed' ? (
                    <Icon name="check" size="sm" />
                  ) : step.status === 'failed' ? (
                    <Icon name="warn" size="sm" />
                  ) : (
                    <span className="ai-deep-search__spinner" />
                  )}
                </span>
                <span className="ai-deep-search__step-query">{step.query}</span>
                {step.resultCount > 0 ? (
                  <span className="sk-tag sk-tag--sm ai-deep-search__count">
                    {step.resultCount} منبع
                  </span>
                ) : null}
              </li>
            ))}
          </ol>
        </div>
      ) : null}

      {hasContradictions ? (
        <div className="ai-deep-search__alert" role="alert">
          <Icon name="warn" size="sm" />
          <div className="ai-deep-search__alert-text">
            <strong>مغایرت‌های شناسایی‌شده میان منابع:</strong>
            <ul className="ai-deep-search__alert-list">
              {spec.contradictions!.map((c, i) => (
                <li key={i}>{c}</li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}

      {hasWebSources ? (
        <div className="ai-deep-search__sources">
          <div className="ai-deep-search__sources-title">منابع وب مورد استناد:</div>
          <ul className="ai-deep-search__sources-list">
            {spec.webSources!.map((source, index) => (
              <li key={index} className="ai-deep-search__source-item">
                <a
                  href={source.url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="ai-link ai-deep-search__link"
                  title={source.snippet}
                >
                  {source.title || source.url} ↗
                </a>
                {source.publishedAt ? (
                  <span className="ai-deep-search__date">انتشار: {source.publishedAt}</span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
